import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faTimes, faBook, faPlay } from '@fortawesome/free-solid-svg-icons';

const FlipBookContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 2rem 0;
`;

const FlipBookWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height || '600px'};
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  background: #f8f9fa;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 15px;
`;

const ControlsOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const ControlButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-2px);
  }
`;

const FullscreenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const FullscreenContent = styled.div`
  width: 95%;
  height: 90%;
  max-width: 1200px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid rgba(153, 0, 17, 0.2);
  border-top-color: ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 1rem;
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #f8f9fa;
  color: #666;
  font-size: 1rem;
  gap: 0.25rem;
`;

const Facade = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  cursor: pointer;
  gap: 1.25rem;
  padding: 2rem;
  text-align: center;

  &:hover .play-btn {
    transform: scale(1.08);
    background: ${props => props.theme.colors.primary};
  }
`;

const FacadeIcon = styled.div`
  font-size: 3.5rem;
  color: rgba(255, 255, 255, 0.25);
`;

const FacadeTitle = styled.p`
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const FacadeMeta = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1.75rem;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.08);
  }
`;

const FlipBookTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  position: relative;
  padding-left: 1rem;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 10px;
  }
`;

const FlipBook = ({
  src = "https://heyzine.com/flip-book/e089867c67.html",
  title = "Project Sponsorship Dossier",
  height = "600px",
  showTitle = true,
  showControls = true
}) => {
  const [activated, setActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const activate = () => {
    setActivated(true);
    setIsLoading(true);
  };

  return (
    <>
      <FlipBookContainer>
        {showTitle && <FlipBookTitle>{title}</FlipBookTitle>}
        <FlipBookWrapper height={height}>
          {!activated && (
            <Facade onClick={activate}>
              <FacadeIcon>
                <FontAwesomeIcon icon={faBook} />
              </FacadeIcon>
              <FacadeTitle>{title}</FacadeTitle>
              <FacadeMeta>Interactive PDF</FacadeMeta>
              <PlayButton className="play-btn">
                <FontAwesomeIcon icon={faPlay} />
                Open Flipbook
              </PlayButton>
            </Facade>
          )}

          {activated && isLoading && (
            <LoadingPlaceholder>
              <Spinner />
              Loading flipbook…
            </LoadingPlaceholder>
          )}

          {activated && (
            <StyledIframe
              src={src}
              title={title}
              onLoad={() => setIsLoading(false)}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}

          {showControls && activated && !isLoading && (
            <ControlsOverlay>
              <ControlButton onClick={() => setIsFullscreen(true)} title="Open in fullscreen">
                <FontAwesomeIcon icon={faExpand} />
              </ControlButton>
            </ControlsOverlay>
          )}
        </FlipBookWrapper>
      </FlipBookContainer>

      {isFullscreen && (
        <FullscreenModal onClick={() => setIsFullscreen(false)}>
          <FullscreenContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setIsFullscreen(false)}>
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
              Close
            </CloseButton>
            <StyledIframe
              src={src}
              title={title}
              style={{ height: '100%', borderRadius: '10px' }}
            />
          </FullscreenContent>
        </FullscreenModal>
      )}
    </>
  );
};

export default FlipBook;
